import {FinanceDocument} from 'common'
import {applicationStatus} from 'common/enums'
import AlgoliaClient, {
  FINANCE_ADMIN_PANEL_ALGOLIA_INDEX
} from 'components/AlgoliaSearch'
import InputField from 'components/InputField'
import Pagination from 'components/Pagination'
import ReqNotFoundScreen from 'components/ReqNotFoundScreen'
import {TABLE_PAGINATION_PAGE_SIZE} from 'components/constants'
import {Timestamp} from 'firebase/firestore'
import debounce from 'lodash/debounce'
import isDate from 'lodash/isDate'
import startCase from 'lodash/startCase'
import React, {useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import Layout from '../Layout'
import {Loader} from '../Loader'
import NavBar from '../Navbar'
import withAuthorization from '../Session/withAuthorization'
import Table from '../TableComponent'
import {convertToDate, getFormattedDate, searchWithAlgolia} from '../utils'
import styles from './FinanceApplicationsPage.module.scss'

const FinanceApplications = ({customClass}: {customClass: string}) => {
  const {state} = useLocation()
  const pageFromLocation = state?.page
  const [currentPage, setCurrentPage] = useState<number>(pageFromLocation || 1)
  const [pageCount, setPageCount] = useState<number>(0)
  const [financeData, setFinancesData] = useState<Array<FinanceDocument>>([])
  const [loader, setLoader] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const searchIndex = AlgoliaClient.initIndex(FINANCE_ADMIN_PANEL_ALGOLIA_INDEX)
  const debouncedSearch = React.useRef(
    debounce(searchWithAlgolia<FinanceDocument>, 800)
  )
  const navigate = useNavigate()
  const [dir, setDir] = useState<{key: string; dir: string}>({
    key: '',
    dir: 'asc'
  })
  useEffect(() => {
    searchIndex
      .setSettings({
        customRanking: ['desc(createdAt)']
      })
      .then(() => {
        AlgoliaClient.clearCache()
        searchWithAlgolia<FinanceDocument>({
          searchQuery: '*',
          searchIndex,
          setLoaderState: setLoader,
          setData: setFinancesData,
          setPageCount,
          currentPage,
          sortData: false
        }).then(() => setLoader(false))
      })
  }, [])

  const tableTitles = [
    {title: 'Contact Name', sortBy: () => sortBy(['contactName'])},
    {title: 'Account Name', sortBy: () => sortBy(['accountName'])},
    {title: 'Contact Number'},
    {title: 'Product ID'},
    {title: 'Application Status', sortBy: () => sortBy(['status'])},
    {title: 'Last Modified', sortBy: () => sortBy(['createdAt'])}
  ]

  const getFormattedData = (financeData: FinanceDocument) => {
    const {
      contactName,
      accountName,
      contactNumber,
      status,
      lastUpdatedAt,
      productId,
      financeId
    } = financeData
    const lastUpdatedDate = lastUpdatedAt && convertToDate(lastUpdatedAt as any)
    const lastUpdated = isDate(lastUpdatedDate)
      ? getFormattedDate(lastUpdatedDate)
      : '-'
    const date = isDate(lastUpdatedDate)
      ? Timestamp.fromDate(lastUpdatedDate).seconds
      : '-'
    const documentId = financeId || ''
    return {
      contactName,
      accountName,
      contactNumber,
      status: startCase(status) as applicationStatus,
      lastUpdated,
      productId,
      date,
      sortDate:
        (lastUpdatedDate && Timestamp.fromDate(lastUpdatedDate).seconds) || 0,
      documentId
    }
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value
    setSearchValue(value)
    if (value) {
      debouncedSearch?.current({
        searchQuery: value,
        searchIndex,
        setLoaderState: setLoader,
        setData: setFinancesData,
        setPageCount,
        sortData: false
      })
    } else {
      debouncedSearch?.current({
        searchQuery: '*',
        searchIndex,
        setLoaderState: setLoader,
        setData: setFinancesData,
        setPageCount,
        currentPage,
        sortData: false
      })
    }
  }

  const getRowData = (
    rowData: Array<{
      contactName: string
      accountName: string
      contactNumber: string
      productId: string
      status: applicationStatus
      lastUpdated: string
      date: string | number
      documentId: string
    }>
  ) => {
    return (rowData || []).map((singleRowData, index) => {
      const {
        contactName,
        accountName,
        contactNumber,
        status,
        productId,
        lastUpdated,
        documentId
      } = singleRowData
      return (
        <tr
          key={`finance-row-data-${index}`}
          onClick={() => {
            navigate('/finance-applications/finance-application-details', {
              state: {id: documentId, currentPage},
              preventScrollReset: true
            })
          }}
        >
          <td>{contactName || '-'}</td>
          <td>{accountName || '-'}</td>
          <td>{contactNumber || '-'}</td>
          <td>{productId || '-'}</td>
          <td>{status || '-'}</td>
          <td>{lastUpdated || '-'}</td>
        </tr>
      )
    })
  }

  const noData = !financeData.length && !loader

  const scrollRef: React.RefObject<HTMLDivElement> = React.createRef()

  const scrollToRef = () =>
    scrollRef?.current?.scrollIntoView({behavior: 'smooth'})

  const items = financeData.map((data) => getFormattedData(data))

  const showPagination = pageCount > TABLE_PAGINATION_PAGE_SIZE

  const onPageChange = (page: number) => {
    setLoader(true)
    setCurrentPage(page)
    debouncedSearch?.current({
      searchQuery: searchValue,
      searchIndex,
      setLoaderState: setLoader,
      setData: setFinancesData,
      setPageCount,
      currentPage: page,
      sortData: false
    })
    setLoader(false)
    scrollToRef()
  }

  const sortBy = async (keys: Array<string>) => {
    await AlgoliaClient.clearCache()
    setLoader(true)
    let newDir = 'asc'
    if (dir.key === keys[0]) {
      newDir = dir.dir === 'asc' ? 'desc' : 'asc'
    }
    setDir({dir: newDir, key: keys[0]})
    await searchIndex.setSettings({
      customRanking: keys.map((key) => newDir + '(' + key + ')')
    })
    setCurrentPage(1)
    setTimeout(() => {
      debouncedSearch?.current({
        searchQuery: searchValue,
        searchIndex,
        setLoaderState: setLoader,
        setData: setFinancesData,
        setPageCount,
        currentPage: 1,
        sortData: false
      })
    }, 1000)
  }

  return (
    <div className={styles.withLayout}>
      <Layout />
      <div className={[styles.container, customClass].join(' ')}>
        <NavBar title="Finance Applications" />
        <div className={styles.button_filters}>
          <InputField
            name="financeSearchField"
            type="text"
            value={searchValue}
            onChange={handleSearch}
            customClass={styles.inputField}
            placeholder="Search for Filters"
            isSearch={true}
          />
        </div>
        {loader ? (
          <Loader />
        ) : noData ? (
          <ReqNotFoundScreen />
        ) : (
          <React.Fragment>
            <Table titles={tableTitles} rowData={getRowData(items)} />
            {showPagination && (
              <div className="pagination__wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalCount={pageCount}
                  pageSize={TABLE_PAGINATION_PAGE_SIZE}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default withAuthorization(FinanceApplications)
